import React, { useCallback, useContext, useState } from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { Heading, Alert } from '@printdeal/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';

import { ErrorManagement } from '@printdeal/error-management/react';
import OrderDetailContext, { OrderDetailContextAfterLoad } from '../../../contexts/OrderDetailContext';
import { OptionProps, DefaultValueProps } from './types/FileDesignTypes';

import { FileDesignRequestSchema } from './schema/FileDesignRequestSchema';

import { ServiceFactory } from '../../../../../../services/ServiceFactory';
import {
  dispatchAccountEvent,
  DispatchCategoriesAccount,
} from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

import DescribeYourIdea from './DescribeYourIdea';
import DesignOptionsRadio from './DesignOptionsRadio';
import DesignOptionsCheckbox from './DesignOptionsCheckbox';
import Attachments from './Attachments';
import { Service } from '../../../../../../types/product/productDetailPage';
import { findNodeByLocale } from '../../../../../../helpers/translations';

const query = graphql`
  query {
    service: allContentfulService(filter: {name: {eq: "File Design"}}) {
      nodes {
        node_locale
        ...Service
      }
    }
  }`;

type QueryResult = {
  service: {
    nodes: Service[];
  };
};

interface Props {
  orderId: string;
  lineItemId: string;
  serviceLineItemId: string;
  colors: OptionProps[];
  templates: OptionProps[];
  fonts: OptionProps[];
  designDescription: string;
  ideaDescription: string;
  styleDescription: string;
  uploadFileDescription: string;
  setIsLoading: (key: boolean) => void;
  handleCloseModal: (eventName: string) => void;
  dispatchEventCategory?: DispatchCategoriesAccount;
}

const defaultValues:DefaultValueProps = {
  attachments: [],
  describeYourIdea: '',
  fonts: [],
  colors: [],
  template: '',
  fontsObject: {},
  colorsObject: {},
};

const RequestFileDesignForm = ({
  orderId,
  lineItemId,
  serviceLineItemId,
  colors,
  templates,
  fonts,
  designDescription,
  ideaDescription,
  styleDescription,
  uploadFileDescription,
  setIsLoading,
  handleCloseModal,
  dispatchEventCategory = 'order_detail_interaction',
}: Props) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [hasError, setHasError] = useState<boolean>(false);
  const { reFetchOrder } = useContext(OrderDetailContext) as OrderDetailContextAfterLoad;
  const { service } = useStaticQuery<QueryResult>(query);
  const fileDesignService = findNodeByLocale(service.nodes, intl.locale);

  // Fire up react-hook-form, and retrieve the data
  const reactHookForm = useForm<DefaultValueProps>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(FileDesignRequestSchema), // Apply the zodResolver
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = reactHookForm;

  // Define the submit handler
  const onSubmit: SubmitHandler<DefaultValueProps> = useCallback(async (data: any) => {
    if (isSubmitting) {
      // Don't even try to submit more than once.
      return;
    }

    setIsLoading(true);
    setHasError(false);
    dispatchAccountEvent(dispatchEventCategory, 'modal_file_design_submit');

    // Build the requestData payload.
    const requestData = {
      orderId,
      lineItemId,
      customLineItemId: serviceLineItemId,
      specification: data.describeYourIdea,
      attachments: data.attachments,
      fonts: data.fonts,
      colors: data.colors,
      templates: [data.template],
    };

    try {
      const serviceService = ServiceFactory.createServices();
      const designServiceResponse = await queryClient.fetchQuery({
        queryKey: ['order', 'submit-file-design-request', orderId, lineItemId, JSON.stringify(requestData)],
        queryFn: () => serviceService.createZendeskTicketDesignFile(requestData),
      });

      if (designServiceResponse instanceof Error) {
        throw new Error('Submission has error.');
        // Will automatically exit 'try {}'.
      }

      if (!designServiceResponse?.data?.success) {
        throw new Error('Submission was unsuccessful.');
        // Will automatically exit 'try {}'.
      }

      if (reFetchOrder) {
        await reFetchOrder({});
      }
      handleCloseModal('model_file_design_close');
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        extra: {
          requestData: JSON.stringify(requestData),
        },
        tags: {
          fileLocation: 'DesignServiceModal',
        },
        fingerprint: ['order-lineItem-service-fileDesign-submit'],
      });

      setHasError(true);
      dispatchAccountEvent(dispatchEventCategory, 'modal_file_design_submit_error');
    }

    setIsLoading(false);
  }, [
    dispatchEventCategory,
    handleCloseModal,
    isSubmitting,
    lineItemId,
    orderId,
    queryClient,
    reFetchOrder,
    serviceLineItemId,
    setIsLoading,
  ]);

  // Show the form.
  return (
    <FormProvider {...reactHookForm}>
      <form
        key="artworkService-form"
        id="artworkService-form"
        onSubmit={handleSubmit(onSubmit)}
        className="pd-flex pd-flex-col pd-gap-5"
      >
        {
          hasError && (
            <Alert variant="error" className="pd-mb-4">
              <FormattedMessage
                id="account.order.service.any.modal.error.api"
                defaultMessage="Something went wrong. Please try after sometime."
              />
            </Alert>
          )
        }
        <section className="pd-flex pd-flex-col pd-gap-4">
          <Heading size="3">
            <FormattedMessage
              id="account.order.artwork.service.modal.body.title"
              defaultMessage="Tell us more about your idea!"
            />
          </Heading>
          <p>{designDescription}</p>
          {fileDesignService?.readMore?.path && (
          <p className="pd-text-dim-500">
            <FormattedMessage
              id="account.order.artwork.service.modal.body.title.info"
              defaultMessage="Do you want to know more about this design process? Check out our {page}"
              values={{
                page: (
                  <Link to={fileDesignService.readMore.path} className="pd-text-primary-600">
                    <FormattedMessage id="account.order.artwork.service.modal.body.title.link" defaultMessage="Professional Online Design page" />
                  </Link>
                ),
              }}
            />
          </p>
          )}
        </section>
        <hr />
        <DescribeYourIdea
          ideaDescription={ideaDescription}
          dispatchEventCategory={dispatchEventCategory}
        />
        <hr />
        <section className="pd-flex pd-flex-col pd-gap-4">
          <Heading size="4">
            <FormattedMessage
              id="account.order.artwork.service.modal.body.step.two.title"
              defaultMessage="Step 2: Choose fonts, colors and styles"
            />
          </Heading>
          <p>{styleDescription}</p>
          <DesignOptionsCheckbox
            title={intl.formatMessage({
              id: 'account.order.artwork.service.modal.body.font.label',
              defaultMessage: 'What kind of font(s) can we use?',
            })}
            options={fonts}
            fieldName="fonts"
            maxVal={2}
            dispatchEventCategory={dispatchEventCategory}
          />
          <DesignOptionsCheckbox
            title={intl.formatMessage({
              id: 'account.order.artwork.service.modal.body.colors.label',
              defaultMessage: 'Which of these colors appeal to you?',
            })}
            options={colors}
            fieldName="colors"
            maxVal={3}
            dispatchEventCategory={dispatchEventCategory}
          />
          <DesignOptionsRadio
            title={intl.formatMessage({
              id: 'account.order.artwork.service.modal.body.templates.label',
              defaultMessage: 'Which of these styles appeal to you?',
            })}
            options={templates}
            fieldName="template"
            dispatchEventCategory={dispatchEventCategory}
          />
        </section>
        <hr />
        <Attachments uploadFileDescription={uploadFileDescription} />
      </form>
    </FormProvider>
  );
};

export default RequestFileDesignForm;
